/**
 * Runtime configuration (docs/modules/infra.md → web agents #1).
 *
 * Precedence: `window.__WINDROP_CONFIG__` (written by `/config.js`, injected by nginx at container start from
 * `API_ORIGIN` / `WEB_ORIGIN` / `BUILD_SHA`) → `import.meta.env.VITE_*` (dev fallback) → defaults. Domains are
 * activation inputs (D13) and are never baked into the bundle. `process.env` is never read here (lint).
 */

export interface RuntimeConfig {
  /** Origin of the API (`https://api.example`), no trailing slash. Empty string = same origin (dev proxy). */
  apiOrigin: string;
  webOrigin: string;
  buildSha: string;
  /** Use the in-memory mock client instead of the network (`?mock=1`, `VITE_MOCK_API=1`, or config.js). */
  mockApi: boolean;
  /** Share of sessions that send web vitals (0–1). */
  vitalsSampleRate: number;
  /** Share of sessions that send client analytics batches (0–1); 1 in production. */
  analyticsEnabled: boolean;
}

declare global {
  interface Window {
    __WINDROP_CONFIG__?: Partial<Record<string, unknown>>;
  }
}

const DEFAULTS: RuntimeConfig = {
  apiOrigin: '',
  webOrigin: '',
  buildSha: 'dev',
  mockApi: false,
  vitalsSampleRate: 0.1,
  analyticsEnabled: true,
};

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
const bool = (v: unknown): boolean | undefined =>
  v === true || v === 'true' || v === '1' || v === 1
    ? true
    : v === false || v === 'false' || v === '0' || v === 0
      ? false
      : undefined;
const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

const trimSlash = (s: string) => s.replace(/\/+$/, '');

let cached: RuntimeConfig | null = null;

/** Reads the configuration once; `resetRuntimeConfig()` exists for tests. */
export function getRuntimeConfig(): RuntimeConfig {
  if (cached) return cached;
  const w = typeof window !== 'undefined' ? (window.__WINDROP_CONFIG__ ?? {}) : {};
  const env = import.meta.env ?? {};
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const sameOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const apiOriginRaw =
    str(w.API_ORIGIN) ?? str(w.apiOrigin) ?? str(env.VITE_API_ORIGIN) ?? DEFAULTS.apiOrigin;
  const apiOrigin = trimSlash(apiOriginRaw);
  const mockFromQuery = search?.get('mock');
  cached = {
    apiOrigin: apiOrigin === sameOrigin ? '' : apiOrigin,
    webOrigin: trimSlash(str(w.WEB_ORIGIN) ?? str(w.webOrigin) ?? str(env.VITE_WEB_ORIGIN) ?? sameOrigin),
    buildSha: str(w.BUILD_SHA) ?? str(w.buildSha) ?? str(env.VITE_BUILD_SHA) ?? DEFAULTS.buildSha,
    mockApi:
      (mockFromQuery != null ? bool(mockFromQuery) : undefined) ??
      bool(w.MOCK_API) ??
      bool(w.mockApi) ??
      bool(env.VITE_MOCK_API) ??
      DEFAULTS.mockApi,
    vitalsSampleRate: Math.min(
      1,
      Math.max(0, num(w.VITALS_SAMPLE_RATE) ?? num(env.VITE_VITALS_SAMPLE_RATE) ?? DEFAULTS.vitalsSampleRate),
    ),
    analyticsEnabled:
      bool(w.ANALYTICS_ENABLED) ?? bool(env.VITE_ANALYTICS_ENABLED) ?? DEFAULTS.analyticsEnabled,
  };
  return cached;
}

/** Test hook. */
export function resetRuntimeConfig(): void {
  cached = null;
}

/** Absolute URL for an API path (`/v1/...`). */
export function apiUrl(path: string): string {
  const { apiOrigin } = getRuntimeConfig();
  return `${apiOrigin}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Adds `<link rel="preconnect">` for the API origin when it differs from the page origin (§13.6 — the first
 * data request should not pay DNS + TLS after the bundle). Idempotent; a no-op outside the browser.
 */
export function preconnectApi(): void {
  if (typeof document === 'undefined') return;
  const { apiOrigin } = getRuntimeConfig();
  if (!apiOrigin || document.querySelector(`link[rel="preconnect"][href="${apiOrigin}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = apiOrigin;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}
